import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';

import PageHeader from '../../components/layout/PageHeader';
import { useRegisterWidget } from '../../components/layout/RealtimeProvider';
import { fullName } from '../../lib/fullName';
import { daysSince } from '../../lib/commitments.helpers';
import { getApiErrorMessage } from '../../lib/apiError';
import { humanize } from '../../lib/stringCase';
import authService from '../../services/auth.service';
import agentDashboardService, {
  type AlertItem,
  type AppointmentItem,
  type CaseItem,
  type DashboardKPIs,
  type DepositItem,
} from '../../services/agent.dashboard.service';
import CallbackDismissForm from '../../components/agent/CallbackDismissForm';
import workQueueService, {
  dismissReasonProblem,
  type PendingCallback,
  type StaleClient,
  type WorkQueue,
  type WorkQueueTask,
} from '../../services/workQueue.service';
import tasksService from '../../services/tasks.service';
import { useAuthStore } from '../../stores/authStore';
import { colors } from '../../styles/theme';
import { readerLocale } from '../../i18n/language';
import '../../styles/agent-dashboard.css';
import WaitingForMe from '../../components/staff/WaitingForMe';
import { formatCents } from '../../lib/amount';

/**
 * Agent Dashboard — rewritten 2026-05-24 per GitLab #63.
 *
 * The architecture is: ONE "Act now" queue (left 60%) where the agent
 * sees every work item that's actionable today — appointments, pending
 * callbacks, due tasks, and overdue tasks — ordered by urgency. The
 * right column (40%) holds passive awareness: approvals you're waiting
 * on, your pending case list (top 5 + drill-in), expiring commitments,
 * alerts, and stale clients (collapsed).
 *
 * Every row in Act-now shows the client phone (`tel:`) and email
 * (`mailto:`) inline, and a primary CTA that opens its destination in
 * a new tab (`target="_blank"`) so the agent's scroll position in the
 * queue is preserved while they work on the case file in another tab.
 *
 * Callback rows have THREE actions:
 *   - [Log call] → /agent/call-guide/{clientId}?callback={requestId}
 *     in a new tab. The CallGuide page, after a successful interaction
 *     record, also marks the callback `completed`.
 *   - [Voicemail] → expands an inline date+time picker. On confirm,
 *     calls POST /callback-requests/{id}/voicemail which re-opens the
 *     row at the picked time (or escalates at the 5th voicemail).
 *     No CallGuide round-trip — voicemail outcome doesn't generate
 *     conversation notes the way an answered call does.
 *   - [Dismiss] → asks for a reason (3-500 characters) and, on confirm,
 *     calls POST /callback-requests/{id}/dismiss. The request leaves the
 *     queue for good; the client is not notified.
 *
 * Empty state surfaces a stale client to nudge proactive outreach.
 */

// ─── Pending-case row helpers ──────────────────────────────────────

/** Friendly label for a workflow state slug. A queue of onboarding cases
 *  is otherwise a wall of identical `onboarding_retail · pending profile`
 *  rows; mapping the state to plain language (and dropping the redundant
 *  workflow slug) makes each row scannable. Unknown slugs fall back to a
 *  Title-cased version so a new state never renders as a raw identifier. */
const CASE_STATE_LABELS: Record<string, string> = {
  pending_profile: 'Personal data',
  pending_pep_aml: 'PEP / AML screening',
  pending_documents: 'Documents',
  under_review: 'Under your review',
  pending_decision: 'Awaiting your decision',
  pending_rejection: 'Rejection proposed',
  pending_rejection_review: 'Rejection review',
  remediation_required: 'Remediation',
  restricted: 'Restricted',
};

function caseStateLabel(slug: string): string {
  return (
    CASE_STATE_LABELS[slug] ??
    humanize(slug)
  );
}

/** Compact "how long since last activity" for the right of a case row —
 *  varies row-to-row, so even same-state cases read differently and the
 *  agent can spot the ones going stale. */
function caseAge(iso: string): string {
  const d = daysSince(iso);
  if (d <= 0) return 'today';
  return d === 1 ? '1d ago' : `${d}d ago`;
}

/** Modifier class for an alert row by severity ('' for the default tone). */
function alertSeverityClass(severity: string): string {
  if (severity === 'firm') return '--firm';
  if (severity === 'info') return '--info';
  return '';
}

// ─── Types for the merged Act-now queue ────────────────────────────

/** Urgency buckets, in display order. Lower index = more urgent and
 *  surfaces first in the merged queue. */
type Urgency = 'overdue' | 'now' | 'today' | 'later';

const URGENCY_RANK: Record<Urgency, number> = {
  overdue: 0,
  now: 1,
  today: 2,
  later: 3,
};

const URGENCY_BADGE: Record<Urgency, { emoji: string; color: string; bg: string; label: string }> = {
  overdue: { emoji: '🟠', color: '#9A3412', bg: '#FED7AA', label: 'OVERDUE' },
  now:     { emoji: '🔴', color: '#991B1B', bg: '#FECACA', label: 'NOW' },
  today:   { emoji: '🟡', color: '#854D0E', bg: '#FEF08A', label: 'TODAY' },
  later:   { emoji: '🔵', color: '#1E3A8A', bg: '#DBEAFE', label: 'LATER' },
};

/** Discriminated union of every row type the Act-now queue renders.
 *  Adding a new source (e.g. escalations) means adding a kind here +
 *  a clause in `buildActNowQueue` + a render branch in `ActNowRow`. */
type ActNowItem =
  | {
      kind: 'appointment';
      id: string;
      urgency: Urgency;
      sortKey: number;
      clientId: string;
      clientName: string;
      clientEmail: string | null;
      clientPhone: string | null;
      caseId: string | null;
      purpose: string | null;
      startsAtIso: string;
    }
  | {
      kind: 'callback';
      id: string;
      urgency: Urgency;
      sortKey: number;
      clientId: string;
      clientName: string;
      clientEmail: string;
      clientPhone: string | null;
      clientPhoneVerified: boolean;
      productName: string | null;
      contextNote: string | null;
      voicemailCount: number;
      hoursSince: number;
    }
  | {
      kind: 'task';
      id: string;
      urgency: Urgency;
      sortKey: number;
      title: string;
      kindLabel: string;
      clientName: string | null;
      clientEmail: string | null;
      daysOverdue: number;
      caseId: string | null; // tasks don't carry caseId today; reserved for future
    };

// ─── Builders ──────────────────────────────────────────────────────

const TODAY_END = (): number => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d.getTime();
};

function appointmentUrgency(starts: number): Urgency {
  const now = Date.now();
  // "NOW" window = +/- 30 minutes around the scheduled start
  if (starts <= now + 30 * 60 * 1000 && starts >= now - 30 * 60 * 1000) return 'now';
  if (starts <= TODAY_END()) return 'today';
  return 'later';
}

function callbackUrgency(hoursSince: number, voicemailCount: number): Urgency {
  // Callbacks are a same-day commitment, so they default to "today". Two signals
  // escalate a row up to "now" so it surfaces ahead of the rest of the bucket:
  // 3+ voicemails (repeatedly unreachable — aging in a different sense) and a
  // wait of 4h+ (it has earned a jump to the front of the same-day queue).
  if (voicemailCount >= 3) return 'now';
  if (hoursSince >= 4) return 'now';
  return 'today';
}

function taskUrgency(item: WorkQueueTask): Urgency {
  if (item.days_overdue > 0) return 'overdue';
  return 'today';
}

function buildActNowQueue(
  appointments: AppointmentItem[],
  callbacks: PendingCallback[],
  tasks: WorkQueueTask[],
): ActNowItem[] {
  const items: ActNowItem[] = [];

  for (const a of appointments) {
    const startsMs = new Date(a.starts_at).getTime();
    const urgency = appointmentUrgency(startsMs);
    items.push({
      kind: 'appointment',
      id: a.id,
      urgency,
      sortKey: startsMs,
      clientId: a.member.id,
      clientName: fullName(a.member) || a.member.email || 'Unnamed client',
      clientEmail: a.member.email ?? null,
      // AppointmentParty doesn't expose phone today. Tracked as a small
      // follow-up: extend the upcoming-appointments endpoint to join
      // identity.users.phone so the Act-now row shows tel: directly.
      clientPhone: null,
      caseId: a.case_id ?? null,
      purpose: a.purpose ?? null,
      startsAtIso: a.starts_at,
    });
  }

  for (const c of callbacks) {
    items.push({
      kind: 'callback',
      id: c.request_id,
      urgency: callbackUrgency(c.hours_since_requested, c.voicemail_count),
      // Older callbacks sort earlier within their urgency bucket
      sortKey: Date.now() - c.hours_since_requested * 3600 * 1000,
      clientId: c.client_id,
      clientName: c.client_name,
      clientEmail: c.client_email,
      clientPhone: c.client_phone,
      clientPhoneVerified: c.client_phone_verified,
      productName: c.product_name,
      contextNote: c.context_note,
      voicemailCount: c.voicemail_count,
      hoursSince: c.hours_since_requested,
    });
  }

  for (const t of tasks) {
    items.push({
      kind: 'task',
      id: t.task_id,
      urgency: taskUrgency(t),
      // Most-overdue tasks sort first within "overdue"; recent tasks
      // sort by oldest first.
      sortKey: -t.days_overdue,
      title: t.title,
      kindLabel: humanize(t.kind),
      clientName: t.client_name,
      clientEmail: t.client_email,
      daysOverdue: t.days_overdue,
      caseId: null, // agent_tasks lacks a direct case link today
    });
  }

  items.sort((a, b) => {
    const rank = URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency];
    if (rank !== 0) return rank;
    return a.sortKey - b.sortKey;
  });
  return items;
}

// ─── Misc helpers ──────────────────────────────────────────────────

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(readerLocale(), { hour: '2-digit', minute: '2-digit' });
}

function formatRelativeDate(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  if (sameDay) return `Today ${formatTime(iso)}`;
  return `${d.toLocaleDateString(readerLocale(), { month: 'short', day: 'numeric' })} ${formatTime(iso)}`;
}

function formatHoursSince(hours: number): string {
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${Math.round(hours)}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function tomorrowAt10Local(): string {
  // ISO local-datetime string in the format <input type="datetime-local">
  // expects: "YYYY-MM-DDTHH:MM" (no offset).
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  const pad = (n: number): string => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ─── Dashboard state ───────────────────────────────────────────────

interface DashboardState {
  loading: boolean;
  error: string | null;
  agentId: string | null;
  appointments: AppointmentItem[];
  cases: CaseItem[];
  approvals: DepositItem[];
  workQueue: WorkQueue | null;
  /** Panels whose last refresh failed; they still show their previous data. */
  staleWidgets: readonly StaleWidget[];
}

const INITIAL_STATE: DashboardState = {
  loading: true,
  error: null,
  agentId: null,
  appointments: [],
  cases: [],
  approvals: [],
  workQueue: null,
  staleWidgets: [],
};

type StaleWidget = 'appointments' | 'cases' | 'approvals' | 'workQueue';

const STALE_WIDGET_LABEL: Readonly<Record<StaleWidget, string>> = {
  appointments: 'Upcoming appointments',
  cases: 'Pending cases',
  approvals: 'Awaiting decision',
  workQueue: 'Act now',
};

function withStale(s: DashboardState, widget: StaleWidget): DashboardState {
  return s.staleWidgets.includes(widget)
    ? s
    : { ...s, staleWidgets: [...s.staleWidgets, widget] };
}

function withoutStale(s: DashboardState, widget: StaleWidget): DashboardState {
  return s.staleWidgets.includes(widget)
    ? { ...s, staleWidgets: s.staleWidgets.filter((w) => w !== widget) }
    : s;
}

/** Names the panels that could not refresh. Nothing renders when all are fresh. */
function StaleWidgetsNotice({
  widgets,
}: Readonly<{ widgets: readonly StaleWidget[] }>): JSX.Element | null {
  if (widgets.length === 0) return null;
  const names = widgets.map((w) => STALE_WIDGET_LABEL[w]).join(', ');
  return (
    <output
      style={{
        display: 'block',
        background: colors.warningBg,
        border: '1px solid #FCD34D',
        borderRadius: 10,
        padding: '10px 14px',
        margin: '0 0 16px',
        fontSize: 13,
        color: colors.ink,
      }}
    >
      Some panels could not refresh and may be out of date: {names}. They update on
      the next change, or reload the page to try now.
    </output>
  );
}

export default function AgentDashboard(): JSX.Element {
  const { user } = useAuthStore();
  const [state, setState] = useState<DashboardState>(INITIAL_STATE);

  // ─── Per-widget refetchers ─────────────────────────────────
  const refetchAppointments = useCallback(async () => {
    try {
      const items = await agentDashboardService.fetchUpcomingAppointments();
      setState((s) => ({ ...withoutStale(s, 'appointments'), appointments: items }));
    } catch (err) {
      console.warn('refetchAppointments failed', err);
      // Keep the last data but flag it: silently showing a stale panel let an
      // agent work a queue that was no longer real.
      setState((s) => withStale(s, 'appointments'));
    }
  }, []);

  const refetchCases = useCallback(async () => {
    try {
      const items = await agentDashboardService.fetchPendingCases();
      setState((s) => ({ ...withoutStale(s, 'cases'), cases: items }));
    } catch (err) {
      console.warn('refetchCases failed', err);
      // Keep the last data but flag it: silently showing a stale panel let an
      // agent work a queue that was no longer real.
      setState((s) => withStale(s, 'cases'));
    }
  }, []);

  const refetchApprovals = useCallback(async () => {
    try {
      const items = await agentDashboardService.fetchPendingApprovals();
      setState((s) => ({ ...withoutStale(s, 'approvals'), approvals: items }));
    } catch (err) {
      console.warn('refetchApprovals failed', err);
      // Keep the last data but flag it: silently showing a stale panel let an
      // agent work a queue that was no longer real.
      setState((s) => withStale(s, 'approvals'));
    }
  }, []);

  const refetchWorkQueue = useCallback(async () => {
    try {
      const id = await authService.getMyId();
      const workQueue = await workQueueService.fetch(id);
      setState((s) => ({ ...withoutStale(s, 'workQueue'), workQueue }));
    } catch (err) {
      console.warn('refetchWorkQueue failed', err);
      setState((s) => withStale(s, 'workQueue'));
    }
  }, []);

  // ─── Initial load ──────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const agentId = await authService.getMyId();
        // The work queue degrades on its own so the rest of the dashboard still
        // renders, but its failure is recorded rather than turned into an empty
        // queue that an agent would read as "nothing to do".
        const loadIssues = { workQueue: false };
        const [appointments, cases, approvals, workQueue] = await Promise.all([
          agentDashboardService.fetchUpcomingAppointments(),
          agentDashboardService.fetchPendingCases(),
          agentDashboardService.fetchPendingApprovals(),
          workQueueService.fetch(agentId).catch((err: unknown) => {
            console.warn('work queue load failed', err);
            loadIssues.workQueue = true;
            return null;
          }),
        ]);
        if (cancelled) return;
        setState({
          loading: false,
          error: null,
          agentId,
          appointments,
          cases,
          approvals,
          workQueue,
          staleWidgets: loadIssues.workQueue ? ['workQueue'] : [],
        });
      } catch (err) {
        if (cancelled) return;
        setState((s) => ({
          ...s,
          loading: false,
          error: getApiErrorMessage(err, 'We could not load your dashboard.'),
        }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ─── SSE wiring ────────────────────────────────────────────
  const refetchAll = useCallback(async () => {
    await Promise.all([refetchAppointments(), refetchCases(), refetchApprovals(), refetchWorkQueue()]);
  }, [refetchAppointments, refetchCases, refetchApprovals, refetchWorkQueue]);

  useRegisterWidget('upcoming-appointments', refetchAppointments);
  useRegisterWidget('pending-cases', refetchCases);
  useRegisterWidget('overdue-followups', refetchCases);
  useRegisterWidget('pending-approvals', refetchApprovals);
  useRegisterWidget('alerts', refetchCases);
  useRegisterWidget('kpis', refetchAll);
  useRegisterWidget('pending-callbacks', refetchWorkQueue);

  // ─── Derived data ──────────────────────────────────────────
  const overdue = agentDashboardService.filterOverdue(state.cases);
  const alerts: AlertItem[] = agentDashboardService.buildAlerts(state.cases);
  const kpis: DashboardKPIs = agentDashboardService.buildKPIs({
    appointments: state.appointments,
    cases: state.cases,
    overdue,
    approvals: state.approvals,
    alerts,
  });

  // Build the unified Act-now queue from the four sources.
  const actNow = useMemo(
    () =>
      buildActNowQueue(
        state.appointments,
        state.workQueue?.pending_callbacks ?? [],
        state.workQueue?.tasks ?? [],
      ),
    [state.appointments, state.workQueue],
  );

  // Pending cases — top 5 for the right "Be aware" panel.
  const topPendingCases = useMemo(() => state.cases.slice(0, 5), [state.cases]);

  // Empty-state surfaces the oldest-quiet stale client when the
  // Act-now queue is genuinely empty. Falls back to a generic message
  // when there's also no stale client to suggest.
  const emptyStateClient: StaleClient | null = useMemo(() => {
    if (actNow.length > 0) return null;
    const stale = state.workQueue?.stale_clients ?? [];
    // Oldest-since-contact first (already sorted that way server-side).
    return stale[0] ?? null;
  }, [actNow.length, state.workQueue?.stale_clients]);

  // ─── Render branches ───────────────────────────────────────
  if (state.loading) {
    return (
      <div className="ad-root">
        <div className="ad-loading">Loading your dashboard…</div>
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="ad-root">
        <div className="ad-error">Error: {state.error}</div>
      </div>
    );
  }

  return (
    <div className="ad-root">
      <PageHeader
        title="Agent Dashboard"
        subtitle={
          <span>
            Signed in as <span className="font-mono">{user?.email}</span>
            <span className="ad-conn" style={{ marginLeft: 12 }}>
              <span className="ad-dot --sage" /> Live
            </span>
          </span>
        }
        /* Days off + Refresh buttons removed 2026-05-24 per GitLab #62. */
      />

      <WaitingForMe />

      <StaleWidgetsNotice widgets={state.staleWidgets} />

      {/* ─── KPI strip ──────────────────────────────────────── */}
      {/* "Awaiting decision" combines deposit approvals + KYC supervisor
          handoffs — both are "things you submitted that a manager has to
          decide on". Single number on the dashboard so the agent doesn't
          need to triangulate two separate cards. Refreshes via the same
          `kpis` / `pending-cases` SSE refetchers fired by
          `case.transitioned` (see publisher.py:75). */}
      <div className="ad-kpi-grid" data-widget="kpis">
        <KPICard label="Today's appointments" value={kpis.appointmentsToday} hint={`${kpis.appointmentsThisWeek} this week`} variant="sage" />
        <KPICard label="Pending cases" value={kpis.casesPending} hint="assigned to you" variant="slate" />
        <KPICard label="Overdue" value={kpis.overdueCount} hint="past SLA" variant={kpis.overdueCount > 0 ? 'firm' : 'slate'} />
        <KPICard
          label="Awaiting decision"
          value={kpis.approvalsWaiting + kpis.kycHandoffsWaiting}
          hint={`${kpis.approvalsWaiting} deposit${kpis.approvalsWaiting === 1 ? '' : 's'} · ${kpis.kycHandoffsWaiting} KYC`}
          variant="slate"
        />
        <KPICard label="Alerts" value={kpis.alertsCount} hint="needing attention" variant={kpis.alertsCount > 0 ? 'soft' : 'slate'} />
      </div>

      {/* ─── Two-column workspace ───────────────────────────── */}
      <div className="ad-section-grid">
        {/* ─── LEFT (60%) — Act now ─────────────────────────── */}
        <div>
          <section className="ad-section" data-widget="act-now">
            <h2>
              Act now
              {/* */}<span className="ad-section__hint">sorted by urgency</span>
            </h2>

            {actNow.length === 0 ? (
              <ActNowEmptyState client={emptyStateClient} />
            ) : (
              <div className="ad-list">
                {actNow.map((item) => (
                  <ActNowRow
                    key={`${item.kind}:${item.id}`}
                    item={item}
                    onActionDone={refetchWorkQueue}
                  />
                ))}
              </div>
            )}
          </section>
        </div>

        {/* ─── RIGHT (40%) — Be aware ───────────────────────── */}
        <div>
          {/* Awaiting manager approval */}
          <section className="ad-section" data-widget="pending-approvals">
            <h2>
              Awaiting manager approval
              {/* */}<span className="ad-section__hint">Deposits you proposed</span>
            </h2>
            {state.approvals.length === 0 ? (
              <p className="ad-empty">Nothing in queue.</p>
            ) : (
              <div className="ad-list">
                {state.approvals.map((d) => (
                  <div key={d.id} className="ad-row">
                    <div className="ad-row__main">
                      <p className="ad-row__title">
                        <span className="ad-dot --soft" />
                        {fullName({ first_name: d.user_first_name, last_name: d.user_last_name })}
                      </p>
                      <p className="ad-row__sub">
                        {d.product_name} · {formatCents(d.amount)} {d.currency_code}
                      </p>
                    </div>
                    <div className="ad-row__meta">
                      {new Date(d.created_at).toLocaleDateString(readerLocale())}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Pending cases — top 5 + drill-in */}
          <section className="ad-section" data-widget="pending-cases">
            <h2>
              Pending cases
              {/* */}<span className="ad-section__hint">your assigned queue</span>
            </h2>
            {topPendingCases.length === 0 ? (
              <p className="ad-empty">Nothing pending. Nice.</p>
            ) : (
              <>
                <div className="ad-list">
                  {topPendingCases.map((c) => (
                    <Link
                      key={c.case_id}
                      to={`/agent/cases/${c.case_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ad-row"
                      style={{ textDecoration: 'none' }}
                    >
                      <div className="ad-row__main">
                        <p className="ad-row__title">
                          <span className={`ad-dot ${c.needs_my_action ? '--sage' : '--slate'}`} />
                          {fullName({ first_name: c.client_first_name, last_name: c.client_last_name })}
                        </p>
                        <p className="ad-row__sub">{caseStateLabel(c.current_state_slug)}</p>
                      </div>
                      <div className="ad-row__meta --stack">
                        <span className={`ad-pill ${c.needs_my_action ? '--sage' : '--slate'}`}>
                          {c.needs_my_action ? 'Needs you' : 'Waiting on client'}
                        </span>
                        <span className="ad-row__age">{caseAge(c.last_activity_at)}</span>
                      </div>
                    </Link>
                  ))}
                </div>
                {state.cases.length > topPendingCases.length && (
                  <Link to="/agent/review-queue" className="ad-viewall">
                    View all {state.cases.length} →
                  </Link>
                )}
              </>
            )}
          </section>

          {/* Expiring commitments */}
          {state.workQueue && (
            <section className="ad-section" data-widget="work-queue-commitments">
              <h2>
                Expiring commitments
                {/* */}<span className="ad-section__hint">within 30 days</span>
              </h2>
              {state.workQueue.expiring_commitments.length === 0 ? (
                <p className="ad-empty">No commitments expiring soon.</p>
              ) : (
                <div className="ad-list">
                  {state.workQueue.expiring_commitments.map((ec) => (
                    <div key={ec.commitment_id} className="ad-row">
                      <div className="ad-row__main">
                        <p className="ad-row__title">
                          <span className={`ad-dot ${ec.days_until_expiry <= 7 ? '--firm' : '--soft'}`} />
                          {ec.client_name}
                        </p>
                        <p className="ad-row__sub">
                          {ec.product_name} · {formatCents(ec.amount)} {ec.currency}
                        </p>
                      </div>
                      <div className="ad-row__meta">
                        {ec.days_until_expiry === 0 ? 'Expires today' : `${ec.days_until_expiry}d left`}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* KYC update needed — "be aware" bucket. These clients can
              still move money; only new contracts are blocked. The agent's
              job is to ask for the re-upload on next contact. */}
          {state.workQueue && state.workQueue.expired_kyc.length > 0 && (
            <section className="ad-section" data-widget="work-queue-expired-kyc">
              <h2>
                KYC update needed
                {/* */}<span className="ad-section__hint">ask for re-upload on next contact</span>
              </h2>
              <div className="ad-list">
                {state.workQueue.expired_kyc.map((k) => (
                  <Link
                    key={k.client_id}
                    to={`/agent/call-guide/${k.client_id}`}
                    className="ad-row"
                  >
                    <div className="ad-row__main">
                      <p className="ad-row__title">
                        <span className="ad-dot --soft" />
                        {k.client_name}
                      </p>
                      <p className="ad-row__sub">
                        {k.expired_count === 1
                          ? '1 document lapsed'
                          : `${k.expired_count} documents lapsed`}
                        {' · since '}
                        {new Date(k.earliest_expiry).toLocaleDateString(readerLocale(), {
                          year: 'numeric', month: 'short', day: 'numeric',
                        })}
                      </p>
                    </div>
                    <div className="ad-row__meta">Call guide →</div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Alerts */}
          <section className="ad-section" data-widget="alerts">
            <h2>
              Alerts
              {/* */}<span className="ad-section__hint">SLA, time-off, KYC</span>
            </h2>
            {alerts.length === 0 ? (
              <p className="ad-empty">No alerts.</p>
            ) : (
              <div className="ad-list">
                {alerts.map((a) => (
                  <div
                    key={a.id}
                    className={`ad-alert ${alertSeverityClass(a.severity)}`}
                  >
                    <span className="ad-alert__title">{a.title}</span>
                    <span className="ad-alert__detail">{a.detail}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Stale clients — low priority, collapsed by default */}
          {state.workQueue && state.workQueue.stale_clients.length > 0 && (
            <StaleClientsSection clients={state.workQueue.stale_clients} />
          )}

          {/* Overdue cases section — kept as fallback signal that
              isn't covered by tasks. Rare but valuable. */}
          {overdue.length > 0 && (
            <section className="ad-section" data-widget="overdue-followups">
              <h2>
                Overdue cases
                {/* */}<span className="ad-section__hint">past SLA</span>
              </h2>
              <div className="ad-list">
                {overdue.map((c) => (
                  <Link
                    key={c.case_id}
                    to={`/agent/cases/${c.case_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ad-row"
                    style={{ textDecoration: 'none' }}
                  >
                    <div className="ad-row__main">
                      <p className="ad-row__title">
                        <span className="ad-dot --firm" />
                        {fullName({ first_name: c.client_first_name, last_name: c.client_last_name })}
                      </p>
                      <p className="ad-row__sub">
                        Expired {c.expires_at ? new Date(c.expires_at).toLocaleDateString(readerLocale()) : 'unknown'} · {c.workflow_slug}
                      </p>
                    </div>
                    <div className="ad-row__meta">{humanize(c.current_state_slug)}</div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Act-now row renderer ──────────────────────────────────────────

interface ActNowRowProps {
  item: ActNowItem;
  onActionDone: () => Promise<void> | void;
}

function ActNowRow({ item, onActionDone }: Readonly<ActNowRowProps>): JSX.Element {
  if (item.kind === 'appointment') return <AppointmentRow item={item} />;
  if (item.kind === 'callback')    return <CallbackRow item={item} onActionDone={onActionDone} />;
  return <TaskRow item={item} onActionDone={onActionDone} />;
}

function UrgencyBadge({ urgency }: { readonly urgency: Urgency }): JSX.Element {
  const b = URGENCY_BADGE[urgency];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 6,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.05em',
        background: b.bg,
        color: b.color,
      }}
    >
      {b.label}
    </span>
  );
}

function ContactLine({
  phone,
  email,
  phoneVerified,
}: {
  readonly phone: string | null;
  readonly email: string | null;
  /** Omit where the source does not say (appointments). When given, the advisor
   *  sees whether the number they are about to dial was ever proven to reach
   *  this client, or is merely something someone typed. */
  readonly phoneVerified?: boolean;
}): JSX.Element | null {
  if (!phone && !email) return null;
  return (
    <div style={{ display: 'flex', gap: 12, marginTop: 4, fontSize: 12, color: colors.inkSoft, flexWrap: 'wrap' }}>
      {phone && (
        <a
          href={`tel:${phone.replace(/\s+/g, '')}`}
          style={{ color: colors.navy, textDecoration: 'none' }}
        >
          📞 {phone}
        </a>
      )}
      {phone && phoneVerified !== undefined && (
        <span
          title={phoneVerified
            ? 'The client confirmed this number with a one-time code.'
            : 'Nobody has confirmed this number reaches the client. Verify identity before discussing the account.'}
          style={{ color: phoneVerified ? colors.success : colors.warning }}
        >
          {phoneVerified ? 'verified' : 'not verified'}
        </span>
      )}
      {email && (
        <a
          href={`mailto:${email}`}
          style={{ color: colors.navy, textDecoration: 'none' }}
        >
          ✉ {email}
        </a>
      )}
    </div>
  );
}

function AppointmentRow({ item }: { readonly item: Extract<ActNowItem, { kind: 'appointment' }> }): JSX.Element {
  const dest = item.caseId ? `/agent/cases/${item.caseId}` : '/agent/appointments';
  return (
    <div className="ad-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
        <div className="ad-row__main">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <UrgencyBadge urgency={item.urgency} />
            <span style={{ fontSize: 12, color: colors.muted }}>📅 Appointment · {formatRelativeDate(item.startsAtIso)}</span>
          </div>
          <p className="ad-row__title" style={{ margin: 0 }}>{item.clientName}</p>
          {item.purpose && <p className="ad-row__sub">{item.purpose}</p>}
          <ContactLine phone={item.clientPhone} email={item.clientEmail} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
          <Link
            to={dest}
            target="_blank"
            rel="noopener noreferrer"
            style={primaryBtnStyle()}
          >
            Open case ↗
          </Link>
          {/* The call screen of the client: also for a call claimed to cover an absent
              colleague, whose case may not be the agent's to open. */}
          <Link
            to={`/agent/call-guide/${item.clientId}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 12, color: colors.inkSoft }}
          >
            Call guide ↗
          </Link>
        </div>
      </div>
    </div>
  );
}

interface TaskRowProps {
  readonly item: Extract<ActNowItem, { kind: 'task' }>;
  readonly onActionDone: () => Promise<void> | void;
}

function TaskRow({ item, onActionDone }: TaskRowProps): JSX.Element {
  const overdue = item.daysOverdue > 0;
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleComplete = async (): Promise<void> => {
    if (completing) return;
    setError(null);
    setCompleting(true);
    try {
      await tasksService.markComplete(item.id);
      // Parent refetches the work queue so the row disappears.
      await onActionDone();
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not mark the task complete.'));
      setCompleting(false);
    }
  };

  return (
    <div className="ad-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
        <div className="ad-row__main">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <UrgencyBadge urgency={item.urgency} />
            <span style={{ fontSize: 12, color: colors.muted }}>
              ✅ Task · {overdue ? `${item.daysOverdue}d overdue` : 'due today'}
            </span>
          </div>
          <p className="ad-row__title" style={{ margin: 0 }}>{item.title}</p>
          <p className="ad-row__sub">
            {item.kindLabel}
            {item.clientName ? ` · ${item.clientName}` : ''}
          </p>
          <ContactLine phone={null} email={item.clientEmail} />
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button
            type="button"
            onClick={handleComplete}
            disabled={completing}
            style={{
              ...primaryBtnStyle(),
              background: colors.successBg,
              color: colors.success,
              border: `1px solid ${colors.successBorder}`,
              cursor: completing ? 'wait' : 'pointer',
              opacity: completing ? 0.7 : 1,
            }}
          >
            {completing ? 'Marking…' : '✓ Mark complete'}
          </button>
          <Link
            to="/agent/review-queue"
            target="_blank"
            rel="noopener noreferrer"
            style={primaryBtnStyle()}
          >
            Open task ↗
          </Link>
        </div>
      </div>
      {error && (
        <p style={{ margin: '6px 0 0', fontSize: 11, color: colors.danger }}>
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Callback row (inline voicemail picker + Log call CTA) ─────────

type CallbackRowItem = Extract<ActNowItem, { kind: 'callback' }>;

interface CallbackRowProps {
  item: CallbackRowItem;
  onActionDone: () => Promise<void> | void;
}

type CallbackUi =
  | { kind: 'idle' }
  | { kind: 'voicemail-picker'; nextAttemptLocal: string }
  | { kind: 'dismiss-form'; reason: string; submitting: boolean; error: string | null }
  | { kind: 'submitting' }
  | { kind: 'error'; message: string };

function CallbackRow({ item, onActionDone }: Readonly<CallbackRowProps>): JSX.Element {
  const [ui, setUi] = useState<CallbackUi>({ kind: 'idle' });

  async function handleDismissConfirm(): Promise<void> {
    if (ui.kind !== 'dismiss-form' || ui.submitting) return;
    const { reason } = ui;
    const problem = dismissReasonProblem(reason);
    if (problem !== null) {
      setUi({ kind: 'dismiss-form', reason, submitting: false, error: problem });
      return;
    }
    setUi({ kind: 'dismiss-form', reason, submitting: true, error: null });
    try {
      await workQueueService.dismissCallback(item.id, reason);
      await onActionDone();
    } catch (err) {
      setUi({
        kind: 'dismiss-form',
        reason,
        submitting: false,
        error: getApiErrorMessage(err, "Couldn't dismiss the callback."),
      });
    }
  }

  async function handleVoicemailConfirm(): Promise<void> {
    if (ui.kind !== 'voicemail-picker') return;
    const trimmed = ui.nextAttemptLocal.trim();
    if (!trimmed) {
      setUi({ kind: 'error', message: 'Pick a date and time.' });
      return;
    }
    // <input type="datetime-local"> returns "YYYY-MM-DDTHH:MM" with
    // no offset; new Date() interprets that as local time.
    const target = new Date(trimmed);
    if (Number.isNaN(target.getTime())) {
      setUi({ kind: 'error', message: 'Invalid date/time.' });
      return;
    }
    if (target.getTime() <= Date.now()) {
      setUi({ kind: 'error', message: 'Next attempt must be in the future.' });
      return;
    }
    setUi({ kind: 'submitting' });
    try {
      await workQueueService.recordCallbackVoicemail(item.id, target);
      await onActionDone();
      // The row will disappear from the queue on next render
      // (next_attempt_at filter); no local state cleanup needed.
    } catch (err) {
      setUi({
        kind: 'error',
        message: getApiErrorMessage(err, "Couldn't reschedule the callback."),
      });
    }
  }

  const isSubmitting = ui.kind === 'submitting';
  const isPickerOpen = ui.kind === 'voicemail-picker';
  const pickerValue = isPickerOpen ? ui.nextAttemptLocal : '';
  const isDismissOpen = ui.kind === 'dismiss-form';

  return (
    <div className="ad-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
        <div className="ad-row__main">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2, flexWrap: 'wrap' }}>
            <UrgencyBadge urgency={item.urgency} />
            <span style={{ fontSize: 12, color: colors.muted }}>📞 Callback · {formatHoursSince(item.hoursSince)}</span>
            {item.voicemailCount > 0 && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '2px 6px',
                  borderRadius: 4,
                  background: '#FEF3C7',
                  color: '#92400E',
                }}
              >
                ATTEMPT {item.voicemailCount + 1} OF 5
              </span>
            )}
          </div>
          <p className="ad-row__title" style={{ margin: 0 }}>{item.clientName}</p>
          {item.productName && (
            <p className="ad-row__sub">About {item.productName}</p>
          )}
          {item.contextNote && (
            <p className="ad-row__sub" style={{ fontStyle: 'italic' }}>{item.contextNote}</p>
          )}
          <ContactLine
            phone={item.clientPhone}
            email={item.clientEmail}
            phoneVerified={item.clientPhoneVerified}
          />
        </div>
        {!isPickerOpen && !isDismissOpen && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
            <Link
              to={`/agent/call-guide/${item.clientId}?callback=${item.id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={primaryBtnStyle()}
            >
              Log call ↗
            </Link>
            <button
              type="button"
              onClick={() => setUi({ kind: 'voicemail-picker', nextAttemptLocal: tomorrowAt10Local() })}
              style={ghostBtnStyle(isSubmitting)}
              disabled={isSubmitting}
            >
              Voicemail
            </button>
            <button
              type="button"
              onClick={() => setUi({ kind: 'dismiss-form', reason: '', submitting: false, error: null })}
              style={ghostBtnStyle(isSubmitting)}
              disabled={isSubmitting}
            >
              Dismiss
            </button>
          </div>
        )}
      </div>

      {ui.kind === 'dismiss-form' && (
        <CallbackDismissForm
          requestId={item.id}
          clientName={item.clientName}
          reason={ui.reason}
          submitting={ui.submitting}
          error={ui.error}
          onReasonChange={(value) =>
            setUi({ kind: 'dismiss-form', reason: value, submitting: false, error: null })
          }
          onConfirm={() => { handleDismissConfirm(); }}
          onCancel={() => setUi({ kind: 'idle' })}
        />
      )}

      {isPickerOpen && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <p style={{ fontSize: 12, color: colors.inkSoft, margin: 0 }}>
            When should the callback reappear in your queue?
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="datetime-local"
              value={pickerValue}
              onChange={(e) =>
                setUi({ kind: 'voicemail-picker', nextAttemptLocal: e.target.value })
              }
              style={{
                padding: '6px 10px',
                border: `1px solid ${colors.border}`,
                borderRadius: 6,
                fontSize: 13,
              }}
            />
            <button
              type="button"
              onClick={() => { handleVoicemailConfirm(); }}
              style={primaryBtnStyle()}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving…' : 'Reschedule'}
            </button>
            <button
              type="button"
              onClick={() => setUi({ kind: 'idle' })}
              style={ghostBtnStyle(false)}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
          {item.voicemailCount + 1 >= 5 && (
            <p style={{ fontSize: 11, color: colors.warning, margin: 0 }}>
              ⚠ This will be voicemail #{item.voicemailCount + 1}. After 5 the call escalates to your manager.
            </p>
          )}
        </div>
      )}

      {ui.kind === 'error' && (
        <p style={{ marginTop: 6, fontSize: 12, color: colors.danger }}>{ui.message}</p>
      )}
    </div>
  );
}

// ─── Stale clients (collapsed, right column) ───────────────────────

function StaleClientsSection({ clients }: { readonly clients: StaleClient[] }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? clients : clients.slice(0, 3);
  const hidden = clients.length - visible.length;
  return (
    <section className="ad-section" data-widget="work-queue-stale">
      <h2>
        Stale
        {/* */}<span className="ad-section__hint">no contact in 30+ days · low priority</span>
      </h2>
      <div className="ad-list">
        {visible.map((c) => (
          <div key={c.client_id} className="ad-row">
            <div className="ad-row__main">
              <p className="ad-row__title">
                <span className={`ad-dot ${c.has_open_followup ? '--firm' : '--slate'}`} />
                {c.client_name}
              </p>
              <ContactLine phone={null} email={c.client_email} />
            </div>
            <div className="ad-row__meta">
              {c.days_since_contact == null ? 'Never' : `${c.days_since_contact}d ago`}
            </div>
          </div>
        ))}
      </div>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          style={{
            marginTop: 8,
            fontSize: 12,
            fontWeight: 600,
            color: colors.navy,
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          + {hidden} more ▾
        </button>
      )}
    </section>
  );
}

// ─── Empty state (proactive) ───────────────────────────────────────

interface EmptyStateProps {
  client: StaleClient | null;
}

function ActNowEmptyState({ client }: Readonly<EmptyStateProps>): JSX.Element {
  return (
    <div
      style={{
        padding: 24,
        textAlign: 'center',
        background: colors.surface,
        borderRadius: 12,
        border: `1px dashed ${colors.border}`,
      }}
    >
      <p style={{ fontSize: 16, fontWeight: 700, color: colors.success, marginBottom: 8 }}>
        Inbox zero ✓
      </p>
      {client ? (
        <>
          <p style={{ fontSize: 13, color: colors.inkSoft, marginBottom: 12 }}>
            Want to reach out to a long-quiet client?
          </p>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 14px',
              background: colors.canvas,
              borderRadius: 8,
            }}
          >
            <span style={{ fontWeight: 600, color: colors.ink }}>
              {client.client_name}
            </span>
            <span style={{ fontSize: 12, color: colors.muted }}>
              · {client.days_since_contact ?? '—'}d ago
            </span>
            <a
              href={`mailto:${client.client_email}`}
              style={{ fontSize: 12, color: colors.navy }}
            >
              ✉ {client.client_email}
            </a>
          </div>
        </>
      ) : (
        <p style={{ fontSize: 13, color: colors.muted, margin: 0 }}>
          Nothing to act on right now.
        </p>
      )}
    </div>
  );
}

// ─── Button styles ─────────────────────────────────────────────────

function primaryBtnStyle(): CSSProperties {
  return {
    padding: '6px 12px',
    borderRadius: 6,
    border: 'none',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    background: colors.navy,
    color: '#FFFFFF',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
  };
}

function ghostBtnStyle(disabled: boolean): CSSProperties {
  return {
    padding: '6px 12px',
    borderRadius: 6,
    border: `1px solid ${colors.border}`,
    fontSize: 12,
    fontWeight: 600,
    cursor: disabled ? 'default' : 'pointer',
    background: colors.surface,
    color: colors.inkSoft,
    opacity: disabled ? 0.6 : 1,
    whiteSpace: 'nowrap',
  };
}

// ─── KPI card (unchanged) ──────────────────────────────────────────

interface KPICardProps {
  label: string;
  value: number;
  hint?: string;
  variant?: 'slate' | 'sage' | 'soft' | 'firm';
}

function KPICard({ label, value, hint, variant = 'slate' }: Readonly<KPICardProps>): JSX.Element {
  return (
    <div className={`ad-kpi --${variant}`}>
      <p className="ad-kpi__label">{label}</p>
      <p className="ad-kpi__value">{value}</p>
      {hint && <p className="ad-kpi__hint">{hint}</p>}
    </div>
  );
}
